import {
  MessageCallReceived,
  HistoryResponse,
  isMessageNotReactionOrNotification,
  useIM,
  HistoryMessageItem,
  PServed,
  MessageMarkedAsRead,
  MessageDeletedByOtherEvent,
  MessageUpdatedByOtherEvent,
  MessageUpdatedByYouEvent,
  MessageDeletedByYouEvent,
  MessageStatusType,
  BaseEvent,
  GroupStatusEvent,
  BaseByOtherEvent,
  isCallMessage,
  isMessageDeleted,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  isTextMessage,
  isCallPastMessage,
  MessageCallAppointmentStart,
  MessageCallAppointmentEnd,
  MessageCallRecordCompleted,
  RequestChatHistoryPayload,
  IMQuote,
} from '@wui/im'
import { defineStore, storeToRefs } from 'pinia'
import { computed, reactive, ref, watch, triggerRef, shallowRef } from 'vue'

import { useCallsStore } from '@/entities/calls'
import { useChatHistoryLoading } from '@/entities/chat-handlers'
import { useStoreContacts } from '@/entities/contact'
import { useNotificationItemStore } from '@/entities/notification'
import { useReactionsStore } from '@/entities/reaction'
import { useStoreLastReadEventIds, useStoreRooms } from '@/entities/room'
import { useRoomScheduleStore } from '@/entities/schedule'

import { useAppBadge } from '@/shared/composables/browser-api'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useUserFeatures } from '@/shared/composables/user-features'
import { useIMStore } from '@/shared/store/im'
import { Contact } from '@/shared/types/api'
import { isSystemContactPServed } from '@/shared/utils/constants'
import { createPrivateRoom } from '@/shared/utils/im'
import { insertAtStore } from '@/shared/utils/insert-at-store'
import { yieldToMainThread } from '@/shared/utils/main-thread'
import { updateStatusIcon } from '@/shared/utils/tauri'
import { fromPServedUserIfRoom, pServedIsRoom } from '@/shared/utils/uri'

import {
  collectRankBounds,
  createDefaultRoomHistoryViewport,
  dedupeHistoryByEventId,
  isPendingClientOutgoing,
  LAZY_LOAD_NEWER_BATCH_SIZE,
  LAZY_LOAD_OLDER_BATCH_SIZE,
  RoomHistoryViewport,
  VIEWPORT_AROUND_BATCH_SIZE,
} from './room-history-viewport'

interface EventsByRoomGuid {
  [roomPServed: PServed]: Array<HistoryMessageItem>
}

type Rank = HistoryMessageItem['rank']

interface RankByEventId {
  [eventId: HistoryMessageItem['eventId']]: Rank
}

interface EventByRoomPServedAndRank {
  [roomPServed: PServed]: {
    [rank: Rank]: HistoryMessageItem
  }
}

const REQUEST_HISTORY_MESSAGES_LIMIT = 25

const PRIMARY_TAIL_MAX_RANK_HOLE = 50
/** Лимит пачек type:newer при догоне истории после реконнекта (25 * 40 ≈ 1000 событий) */
const SYNC_NEWER_MAX_BATCHES = 40

export const useChatMessageStore = defineStore('chat-messages', () => {
  const {
    onEditMessage,
    requestDialogs,
    requestChatHistory,
    markAsRead,
    onDeleteMessage,
    onHistory,
    onMarkAsRead,
    onConferenceCreated,
    onConferenceEdited,
    onMessage,
    onPinMessage,
    onUnpinMessage,
  } = useIM()

  /**
   * Plain storage + shallowRef mirror. Fine-grained UI deps use roomMessagesEpoch;
   * peekRoomEvents reads the plain object so list items are not tied to global triggerRef.
   */
  let eventsByRoomPServedData: EventsByRoomGuid = {}
  const eventsByRoomPServed = shallowRef<EventsByRoomGuid>(eventsByRoomPServedData)
  const roomIdByEventId = shallowRef(new Map<HistoryMessageItem['eventId'], PServed>())
  const rankByEventId = shallowRef<RankByEventId>({})
  const eventsById = shallowRef(new Map<HistoryMessageItem['eventId'], HistoryMessageItem>())
  const eventsByForwardingEventId = shallowRef(new Map<HistoryMessageItem['eventId'], HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>>())
  const deletedEventIds = ref(new Map<PServed, HistoryMessageItem['eventId']>())
  const deletedEventsByEventId = shallowRef(new Map<HistoryMessageItem['eventId'], HistoryMessageItem>())
  const eventIdsByQuotedEventId = shallowRef(new Map<HistoryMessageItem['eventId'], Set<HistoryMessageItem['eventId']>>())
  const eventsByRoomPServedAndRank = ref<EventByRoomPServedAndRank>({})
  const roomHistoryViewport = ref<Record<PServed, RoomHistoryViewport>>({})
  const roomMessagesEpoch = reactive(new Map<PServed, number>())
  const lastMessageByPServed = new Map<PServed, HistoryMessageItem | null>()

  const peekRoomEvents = (roomPServed: PServed): HistoryMessageItem[] | undefined => {
    return eventsByRoomPServedData[roomPServed]
  }

  const bumpRoomMessagesEpoch = (pServed: PServed) => {
    lastMessageByPServed.delete(pServed)
    roomMessagesEpoch.set(pServed, (roomMessagesEpoch.get(pServed) ?? 0) + 1)
  }

  const touchRoomMessages = (pServed: PServed) => {
    void roomMessagesEpoch.get(pServed)
  }

  const getRoomHistoryViewport = (pServed: PServed): RoomHistoryViewport => {
    return roomHistoryViewport.value[pServed] ?? createDefaultRoomHistoryViewport()
  }

  const setRoomHistoryViewport = (pServed: PServed, viewport: RoomHistoryViewport): void => {
    roomHistoryViewport.value[pServed] = viewport
  }

  const isEventInViewport = (pServed: PServed, eventId: HistoryMessageItem['eventId']): boolean => {
    const viewport = getRoomHistoryViewport(pServed)
    if (viewport.mode !== 'anchored' || viewport.minRank === null || viewport.maxRank === null) {
      return true
    }

    const message = eventsById.value.get(eventId)
    if (!message?.rank) {
      return false
    }

    return message.rank >= viewport.minRank && message.rank <= viewport.maxRank
  }

  const shouldAcceptRealtimeMessage = (pServed: PServed, rank: number | undefined): boolean => {
    if (rank === undefined) {
      return true
    }

    const viewport = getRoomHistoryViewport(pServed)
    if (viewport.mode !== 'anchored' || viewport.minRank === null || viewport.maxRank === null) {
      return true
    }

    if (rank > viewport.maxRank) {
      return !viewport.hasMoreNewer
    }

    if (rank < viewport.minRank) {
      return false
    }

    return true
  }

  const extendViewportForRank = (pServed: PServed, rank: number): void => {
    const viewport = getRoomHistoryViewport(pServed)
    if (viewport.mode !== 'anchored') {
      return
    }

    const nextViewport: RoomHistoryViewport = { ...viewport }
    if (nextViewport.minRank === null || rank < nextViewport.minRank) {
      nextViewport.minRank = rank
      nextViewport.hasMoreOlder = rank > 1
    }
    if (nextViewport.maxRank === null || rank > nextViewport.maxRank) {
      nextViewport.maxRank = rank
    }
    setRoomHistoryViewport(pServed, nextViewport)
  }

  const indexDeletedEvent = (event: HistoryMessageItem) => {
    deletedEventsByEventId.value.set(event.eventId, event)
  }

  /** Per-room unread; cleared on message storage trigger / last-read changes */
  const unreadCountByPServed = new Map<PServed, number>()

  const invalidateUnreadCount = (pServed?: PServed) => {
    if (pServed === undefined) {
      unreadCountByPServed.clear()
      return
    }
    unreadCountByPServed.delete(pServed)
  }

  const $reset = () => {
    eventsByRoomPServedData = {}
    eventsByRoomPServed.value = eventsByRoomPServedData
    roomIdByEventId.value = new Map()
    rankByEventId.value = {}
    eventsById.value = new Map()
    eventsByForwardingEventId.value = new Map()
    deletedEventsByEventId.value = new Map()
    eventIdsByQuotedEventId.value = new Map()
    eventsByRoomPServedAndRank.value = {}
    roomHistoryViewport.value = {}
    lastMessageByPServed.clear()
    roomMessagesEpoch.clear()
    unreadCountByPServed.clear()

    _triggerStorageRef()
  }

  const { currentUser } = useCurrentUser()
  const { currentGuest } = useGuestAuth()
  const {
    handleDeletedMessage,
    getOwnMessageRankByRoomAndContactId,
    getMarkedUnread,
    updateFromContactStatus,
    updateLastReadEventId,
  } = useStoreLastReadEventIds()

  let _chatStoreIsReadyResolveFn: (value: boolean) => void
  const chatStoreIsReady: Promise<boolean> = new Promise((resolve) => {
    _chatStoreIsReadyResolveFn = resolve
  })

  const pendingForApplyMessagesByEventId: Map<number, HistoryMessageItem<
    MessageUpdatedByOtherEvent |
    MessageUpdatedByYouEvent |
    MessageDeletedByOtherEvent |
    MessageDeletedByYouEvent |
    MessageMarkedAsRead
  >> = new Map()

  const filterVisibleChatEvents = (
    events: HistoryMessageItem[],
    allRoomEvents: HistoryMessageItem[],
  ): HistoryMessageItem[] => {
    return events.filter((message) => {
      if (isMessageDeleted(message)) {
        return false
      }

      let appointmentEnded = true
      const typedItem = message.item as MessageCallAppointmentStart
      if (typedItem.event === 'appointment_start') {
        appointmentEnded = !!allRoomEvents.find(e => {
          const typedEndedItem = e.item as MessageCallAppointmentEnd
          return typedEndedItem.event === 'appointment_end' && typedEndedItem.confId === typedItem.confId
        })
      }

      return message.item.type !== 'MessageCallReceived' ||
        isCallPastMessage(message) ||
        !appointmentEnded
    })
  }

  const findStoredRoomMessages = (roomPServed: PServed): HistoryMessageItem[] => {
    touchRoomMessages(roomPServed)
    const events = peekRoomEvents(roomPServed)
    if (!events) {
      return []
    }

    return filterVisibleChatEvents(events, events)
  }

  const getPrimaryTailSegmentBounds = (
    messages: HistoryMessageItem[],
  ): { minRank: number, maxRank: number } | null => {
    const storedRanks = messages
      .filter((message) => !isPendingClientOutgoing(message))
      .map((message) => message.rank)
      .filter((rank): rank is number => rank !== undefined)

    if (!storedRanks.length) {
      const pendingRanks = messages
        .filter(isPendingClientOutgoing)
        .map((message) => message.rank)
        .filter((rank): rank is number => rank !== undefined)

      if (!pendingRanks.length) {
        return null
      }

      return {
        minRank: Math.min(...pendingRanks),
        maxRank: Math.max(...pendingRanks),
      }
    }

    const uniqueRanksDesc = [...new Set(storedRanks)].sort((left, right) => right - left)
    const maxRank = uniqueRanksDesc[0]
    if (maxRank === undefined) {
      return null
    }

    const minRank = uniqueRanksDesc.reduce((currentMin, nextRank) => {
      if (currentMin - nextRank > PRIMARY_TAIL_MAX_RANK_HOLE) {
        return currentMin
      }
      return nextRank
    }, maxRank)

    return { minRank, maxRank }
  }

  const isVisibleInHistoryViewport = (
    message: HistoryMessageItem,
    minRank: number,
    maxRank: number,
  ): boolean => {
    if (message.rank === undefined) {
      return false
    }

    return (message.rank >= minRank && message.rank <= maxRank)
      || isPendingClientOutgoing(message)
  }

  const findRoomMessages = (roomPServed: PServed): Array<HistoryMessageItem> => {
    touchRoomMessages(roomPServed)
    const allEvents = peekRoomEvents(roomPServed)
    if (!allEvents) {
      return []
    }

    let events = allEvents

    const viewport = getRoomHistoryViewport(roomPServed)
    if (viewport.mode === 'anchored' && viewport.minRank !== null && viewport.maxRank !== null) {
      events = allEvents.filter((message) => {
        return isVisibleInHistoryViewport(message, viewport.minRank!, viewport.maxRank!)
      })
    } else if (viewport.mode === 'tail') {
      const primaryTailBounds = getPrimaryTailSegmentBounds(allEvents)
      if (primaryTailBounds) {
        events = allEvents.filter((message) => {
          return isVisibleInHistoryViewport(message, primaryTailBounds.minRank, primaryTailBounds.maxRank)
        })
      }
    }

    return filterVisibleChatEvents(events, allEvents)
  }

  const findById = <T extends BaseEvent = BaseEvent>(id: HistoryMessageItem['eventId']) => {
    return <HistoryMessageItem<T> | undefined>eventsById.value.get(id)
  }

  const isRecordCompletedEvent = (message: HistoryMessageItem) => {
    const typedMessageRecordCompletedItem = message.item as MessageCallRecordCompleted
    return typedMessageRecordCompletedItem.event === 'call_record_completed'
  }

  const findLastMessage = (roomPServed: PServed): HistoryMessageItem | undefined => {
    touchRoomMessages(roomPServed)
    if (lastMessageByPServed.has(roomPServed)) {
      return lastMessageByPServed.get(roomPServed) ?? undefined
    }

    const last = findStoredRoomMessages(roomPServed).filter(e => !isRecordCompletedEvent(e))?.at(-1)
    lastMessageByPServed.set(roomPServed, last ?? null)
    return last
  }

  const findLastReadableMessageAtOrBefore = (
    roomPServed: PServed,
    eventId: HistoryMessageItem['eventId'],
  ): HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined => {
    return findStoredRoomMessages(roomPServed)
      .filter((event): event is HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> => {
        return (event.item.type === 'IncomingMessageEvent' || event.item.type === 'OutgoingMessageEvent')
          && event.eventId <= eventId
      })
      .at(-1)
  }

  const computeUnreadCount = (pServed: PServed): number => {
    const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(pServed)
    const room = useStoreRooms().findByPServed(pServed)
    if (lastReadMessageRank === undefined || !room?.isEnabled) {
      return 0
    }

    const markedUnread = getMarkedUnread(pServed)
    if (markedUnread) {
      return findStoredRoomMessages(pServed).filter(event => {
        if (isRecordCompletedEvent(event)) {
          return false
        }

        const isAppropriateType = event.item.type !== 'OutgoingMessageEvent'
        const rankIsMoreThanLastRead = !!event.rank && event.rank >= markedUnread.rank
        return isAppropriateType && rankIsMoreThanLastRead
      })?.length ?? 0
    }

    return findStoredRoomMessages(pServed).filter(event => {
      if (isRecordCompletedEvent(event)) {
        return false
      }

      const isAppropriateType = event.item.type !== 'OutgoingMessageEvent'
      const rankIsMoreThanLastRead = !!event.rank && event.rank > lastReadMessageRank
      return isAppropriateType && rankIsMoreThanLastRead
    })?.length ?? 0
  }

  const getUnreadCount = (pServed: PServed): number => {
    // Touch reactive inputs so Vue computeds keep correct deps even on cache hit
    touchRoomMessages(pServed)
    const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(pServed)
    const room = useStoreRooms().findByPServed(pServed)
    void getMarkedUnread(pServed)

    if (lastReadMessageRank === undefined || !room?.isEnabled) {
      return 0
    }

    const cached = unreadCountByPServed.get(pServed)
    if (cached !== undefined) {
      return cached
    }

    const unread = computeUnreadCount(pServed)
    unreadCountByPServed.set(pServed, unread)
    return unread
  }

  const { isChatMessagingUiEnabled, areAccessFeaturesLoaded } = useUserFeatures()
  const { unreadCount: callsUnreadCount } = storeToRefs(useCallsStore())

  // Без чатов: message-unread в badge не поднимаем (нельзя сбросить), пропущенные — из calls
  const totalUnreadCount = computed(() => {
    if (areAccessFeaturesLoaded.value && !isChatMessagingUiEnabled.value) {
      return callsUnreadCount.value
    }
    return Object.keys(eventsByRoomPServed.value)
      .reduce(
        (acc, pServed) => acc + (getUnreadCount(pServed) > 0 ? 1 : 0),
        0,
      )
  })

  /**
   * @param affected - room(s) whose message data changed. Omit for full refresh (dialogs/history/$reset).
   * Scoped updates bump per-room epoch only — sidebar items for other chats stay cold.
   * @param exposeRoomsIndex - also wake consumers of eventsByRoomPServed keys (new room bucket).
   */
  const _triggerStorageRef = (
    affected?: PServed | PServed[],
    options?: { exposeRoomsIndex?: boolean },
  ) => {
    if (affected === undefined) {
      unreadCountByPServed.clear()
      lastMessageByPServed.clear()
      Object.keys(eventsByRoomPServedData).forEach((pServed) => bumpRoomMessagesEpoch(pServed))
      triggerRef(eventsByRoomPServed)
    } else {
      const rooms = Array.isArray(affected) ? affected : [affected]
      rooms.forEach((pServed) => {
        unreadCountByPServed.delete(pServed)
        bumpRoomMessagesEpoch(pServed)
      })
      if (options?.exposeRoomsIndex) {
        triggerRef(eventsByRoomPServed)
      }
    }

    triggerRef(eventsById)
    triggerRef(roomIdByEventId)
    triggerRef(rankByEventId)
    triggerRef(eventsByForwardingEventId)
    triggerRef(eventIdsByQuotedEventId)
  }

  const removeEventFromQuotedIndex = (
    eventId: HistoryMessageItem['eventId'],
    event?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  ) => {
    const sourceEvent = event ?? (eventsById.value.get(eventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined)
    const quotedEventId = sourceEvent?.item.quote?.quotedEventId
    if (!quotedEventId) {
      return
    }

    const quotedEventIds = eventIdsByQuotedEventId.value.get(quotedEventId)
    quotedEventIds?.delete(eventId)
    if (!quotedEventIds?.size) {
      eventIdsByQuotedEventId.value.delete(quotedEventId)
    }
  }

  const addEventToQuotedIndex = (
    eventId: HistoryMessageItem['eventId'],
    event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  ) => {
    const quotedEventId = event.item.quote?.quotedEventId
    if (!quotedEventId) {
      return
    }

    if (!eventIdsByQuotedEventId.value.has(quotedEventId)) {
      eventIdsByQuotedEventId.value.set(quotedEventId, new Set([eventId]))
      return
    }

    eventIdsByQuotedEventId.value.get(quotedEventId)?.add(eventId)
  }

  const removeEventFromForwardingIndex = (
    event?: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  ) => {
    const forwardingEventId = event?.item.forwarding?.forwardingEventId
    if (!forwardingEventId) {
      return
    }

    eventsByForwardingEventId.value.delete(forwardingEventId)
  }

  const addMessage = (pServed: string, message: HistoryMessageItem) => {
    const isNewRoomBucket = !(pServed in eventsByRoomPServedData)
    _addMessageWithoutTriggerRef(pServed, message)

    if (isPendingClientOutgoing(message)) {
      const viewport = getRoomHistoryViewport(pServed)
      if (viewport.mode === 'anchored') {
        setRoomHistoryViewport(pServed, createDefaultRoomHistoryViewport())
      }
    } else if (message.rank !== undefined && shouldAcceptRealtimeMessage(pServed, message.rank)) {
      extendViewportForRank(pServed, message.rank)
      const viewport = getRoomHistoryViewport(pServed)
      if (viewport.mode === 'anchored' && message.rank > (viewport.maxRank ?? 0) && !viewport.hasMoreNewer) {
        setRoomHistoryViewport(pServed, {
          ...viewport,
          maxRank: message.rank,
        })
      }
    }

    _triggerStorageRef(pServed, { exposeRoomsIndex: isNewRoomBucket })
  }

  /**
    * Приватный метод не вызывающий триггер реактивности стора
    *
    * @param pServed
    * @param message
    * @returns
  */
  const _addMessageWithoutTriggerRef = (pServed: string, message: HistoryMessageItem) => {
    const { eventId, rank, item } = message

    if (!eventId || !pServed || !rank) return

    const clonedMessage = { ...message, item: { ...item }} as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
    if (clonedMessage.item.files?.length) {
      clonedMessage.item.files = [...clonedMessage.item.files]
    }
    if (clonedMessage.item.mentionedUsers) {
      clonedMessage.item.mentionedUsers = [...clonedMessage.item.mentionedUsers]
    }

    eventsById.value.set(eventId, clonedMessage)

    const forwardingEventId = clonedMessage.item.forwarding?.forwardingEventId
    if (forwardingEventId) {
      eventsByForwardingEventId.value.set(forwardingEventId, clonedMessage)
    }

    roomIdByEventId.value.set(eventId, pServed)

    if (!eventsByRoomPServed.value[pServed]) {
      eventsByRoomPServed.value[pServed] = []
    }
    if (!eventsByRoomPServedAndRank.value[pServed]) {
      eventsByRoomPServedAndRank.value[pServed] = {}
    }
    insertAtStore<HistoryMessageItem, 'rank'>(eventsByRoomPServed.value[pServed], clonedMessage, 'rank')
    roomIdByEventId.value.set(eventId, pServed)
    rankByEventId.value[eventId] = rank
    eventsById.value.set(eventId, clonedMessage)

    if (isMessageDeleted(message)) {
      // Tombstone из dialogs/history: сохраняем cursor, чтобы prefetch мог догрузить более старые видимые сообщения
      saveDeletedEventId(pServed, eventId)
      indexDeletedEvent(clonedMessage)
    } else {
      eventsByRoomPServedAndRank.value[pServed][rank] = clonedMessage
    }

    if (forwardingEventId) {
      eventsByForwardingEventId.value.set(forwardingEventId, clonedMessage)
    }

    const update = pendingForApplyMessagesByEventId.get(eventId)
    if (update) {
      if (update.item.type === 'UpdateMessageEvent') {
        updateMessageText(update as HistoryMessageItem<MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent>)
      } else if (update.item.type === 'DeleteMessageEvent') {
        _deleteMessage(eventId, update.item.pServed)
      } else if (update.item.type === 'MessageMarkedAsRead') {
        updateMessageStatus(eventId, update.item.pServed, update.item.executor, update.status, update.rank)
      }
      pendingForApplyMessagesByEventId.delete(eventId)
    }

    addEventToQuotedIndex(eventId, clonedMessage)
  }

  const updateMessage = (pServed: PServed, payload: HistoryMessageItem, oldEventId: number, oldRank?: number) => {
    _updateMessageWithoutTriggerRef(pServed, payload, oldEventId)
    _triggerStorageRef(pServed)

    if (oldRank) {
      delete eventsByRoomPServedAndRank.value[pServed]?.[oldRank]
    }
  }

  /**
   * Приватный метод не вызывающий триггер реактивности стора
   *
   * @param pServed
   * @param payload
   * @param oldEventId
   * @returns
   */
  const _updateMessageWithoutTriggerRef = (pServed: PServed, payload: HistoryMessageItem, oldEventId: number) => {
    const { rank, eventId } = payload
    if (!eventId) return
    const oldEvent = eventsById.value.get(oldEventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
    eventsById.value.set(eventId, payload)

    const forwardingEventId = (payload.item as MessageIncomingEvent | MessageOutgoingEvent).forwarding?.forwardingEventId
    if (forwardingEventId) {
      eventsByForwardingEventId.value.set(forwardingEventId, (payload as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>))
    }

    if (rank) {
      rankByEventId.value[eventId] = rank
      eventsByRoomPServedAndRank.value[pServed]![rank] = payload
    } else {
      console.warn(`${pServed} No rank detected during update message`, payload)
    }
    roomIdByEventId.value.set(eventId, pServed)

    const roomMessages = eventsByRoomPServed.value[pServed]
    if (!roomMessages) return

    const index = roomMessages.findIndex((event: HistoryMessageItem) => event.eventId === oldEventId)
    if (index > -1) {
      roomMessages.splice(index, 1)
    } else {
      console.warn(`${pServed} No event detected during update message`, payload)
    }
    insertAtStore<HistoryMessageItem, 'rank'>(roomMessages, payload, 'rank')

    if (eventId !== oldEventId) {
      roomIdByEventId.value.delete(oldEventId)
      eventsById.value.delete(oldEventId)
      delete rankByEventId.value[oldEventId]
    }

    if (oldEvent) {
      removeEventFromQuotedIndex(oldEventId, oldEvent)
      removeEventFromForwardingIndex(oldEvent)
    }

    addEventToQuotedIndex(eventId, payload as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
  }

  const saveDeletedEventId = (pServed: PServed, eventId: HistoryMessageItem['eventId']) => {
    const id = deletedEventIds.value.get(pServed)
    if (!id) {
      deletedEventIds.value.set(pServed, eventId)
    } else if (eventId > id) {
      deletedEventIds.value.set(pServed, eventId)
    }
  }

  const _deleteMessage = (eventId: number, pServed?: string) => {
    if (eventIdsByQuotedEventId.value.has(eventId)) {
      eventIdsByQuotedEventId.value.delete(eventId)
    } else {
      removeEventFromQuotedIndex(eventId)
    }

    if (!pServed) {
      pServed = roomIdByEventId.value.get(eventId)
    }
    if (pServed) {
      saveDeletedEventId(pServed, eventId)
      const eventForDelete = eventsById.value.get(eventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
      removeEventFromForwardingIndex(eventForDelete)
      eventsById.value.delete(eventId)
      _deleteForwardingMessage(eventId)

      const index = eventsByRoomPServed.value[pServed]?.findIndex((event) => event.eventId === eventId)
      if (index !== undefined && index > -1) {
        resetMessage(eventId, pServed)
        eventsByRoomPServed.value[pServed]!.splice(index, 1)
      } else {
        const messageToDelete: MessageDeletedByOtherEvent = {
          eventId,
          from: pServed,
          pServed,
          rank: 0,
          targetEventId: eventId,
          timestamp: 0,
          type: 'DeleteMessageEvent',
        }
        pendingForApplyMessagesByEventId.set(eventId, {
          eventId,
          rank: 0,
          status: MessageStatusType.DELETED,
          timestamp: 0,
          type: 0,
          item: messageToDelete,
        })
      }

      findRoomMessages(pServed).forEach((m) => {
        const typed = { ...(m as HistoryMessageItem<MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent>) }
        if (typed.item.quote?.quotedEventId === eventId) {
          typed.item = { ...typed.item, quote: {} as IMQuote }
          updateMessageText(typed)
        }
      })
      _triggerStorageRef(pServed)
      return
    }
    _triggerStorageRef()
  }

  const resetMessage = (eventId: number, pServed?: string) => {
    if (!pServed) {
      pServed = roomIdByEventId.value.get(eventId)
    }
    if (pServed) {
      const idx = eventsByRoomPServed.value[pServed]?.findIndex((e) => e.eventId === eventId)
      if (idx !== undefined && idx >= 0) {
        const event = { ...eventsByRoomPServed.value[pServed]![idx!] } as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
        if (event) {
          event.item.messageText = ''
          event.item.files = []
          event.status = MessageStatusType.DELETED
          eventsByRoomPServed.value[pServed]![idx] = event
          useReactionsStore().deleteReactionsFromStoreByEventId(eventId)
          delete eventsByRoomPServedAndRank.value[pServed]![event.rank!]
          indexDeletedEvent(event)

          const anotherEvent = eventsById.value.get(eventId)
          if (anotherEvent) {
            anotherEvent.status = MessageStatusType.DELETED
          }

          _triggerStorageRef(pServed)
        }
      }
    }
  }

  const hideMessageForSelf = (eventId: number, pServed?: string) => {
    _deleteMessage(eventId, pServed)
  }

  const _deleteForwardingMessage = (eventId: number) => {
    const event = eventsByForwardingEventId.value.get(eventId)
    if (event && event.item.forwarding) {
      const updated = { ...event, item: { ...event.item, forwarding: {
        forwardingText: '',
        forwardingFiles: [],
      }}}
      eventsByForwardingEventId.value.set(eventId, updated)
    }
    eventsByForwardingEventId.value.delete(eventId)
    triggerRef(eventsByForwardingEventId)
  }

  const deleteMessages = (pServed: PServed) => {
    eventsByRoomPServed.value[pServed]!.forEach((event) => {
      const eventId = event.eventId
      if (eventId) {
        removeEventFromQuotedIndex(eventId, event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
        removeEventFromForwardingIndex(event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
        eventsById.value.delete(eventId)
        deletedEventsByEventId.value.delete(eventId)
        _deleteForwardingMessage(eventId)
        pendingForApplyMessagesByEventId.delete(eventId)
        roomIdByEventId.value.delete(eventId)
        delete rankByEventId.value[eventId]
      }
    })
    delete eventsByRoomPServed.value[pServed]
    delete eventsByRoomPServedData[pServed]
    delete eventsByRoomPServedAndRank.value[pServed]
    delete roomHistoryViewport.value[pServed]
    _triggerStorageRef(pServed, { exposeRoomsIndex: true })
  }

  const clearRoomMessages = (pServed: PServed): void => {
    const roomMessages = eventsByRoomPServed.value[pServed]
    if (!roomMessages?.length) {
      delete eventsByRoomPServed.value[pServed]
      delete eventsByRoomPServedAndRank.value[pServed]
      return
    }

    roomMessages.forEach((event) => {
      const eventId = event.eventId
      if (!eventId) {
        return
      }

      removeEventFromQuotedIndex(eventId, event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
      removeEventFromForwardingIndex(event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
      eventsById.value.delete(eventId)
      deletedEventsByEventId.value.delete(eventId)
      _deleteForwardingMessage(eventId)
      pendingForApplyMessagesByEventId.delete(eventId)
      roomIdByEventId.value.delete(eventId)
      delete rankByEventId.value[eventId]

      if (event.rank !== undefined) {
        delete eventsByRoomPServedAndRank.value[pServed]?.[event.rank]
      }
    })

    delete eventsByRoomPServed.value[pServed]
    delete eventsByRoomPServedData[pServed]
    delete eventsByRoomPServedAndRank.value[pServed]
    _triggerStorageRef(pServed, { exposeRoomsIndex: true })
  }

  const updateMessageText = (payload: HistoryMessageItem<MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent>) => {
    const { item, item: { pServed, era }} = payload
    const eventId = item.targetEventId
    if (pServed) {
      const index = eventsByRoomPServed.value[pServed]?.findIndex((event) => event.eventId === eventId)
      if (index !== undefined && index >= 0) {
        const event = { ...eventsByRoomPServed.value[pServed]![index!] as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> }

        event.item.messageText = item.messageText
        event.item.files = item.files ?? []
        event.item.quote = item.quote ?? {} as IMQuote
        if (item.entities?.length) {
          event.item.entities = item.entities
        } else {
          delete event.item.entities
        }
        event.item.era = era
        eventsByRoomPServed.value[pServed]![index] = event
        eventsById.value.set(eventId, event)
        if (event.rank !== undefined && eventsByRoomPServedAndRank.value[pServed]?.[event.rank]) {
          eventsByRoomPServedAndRank.value[pServed]![event.rank] = event
        }
      } else {
        const event = pendingForApplyMessagesByEventId.get(eventId)
        if (event) {
          const eventItem = {
            ...event.item,
            messageText: item.messageText,
            files: item.files,
            forwarding: item.forwarding,
            quote: item.quote,
          } as MessageUpdatedByOtherEvent | MessageUpdatedByYouEvent
          if (item.entities?.length) {
            eventItem.entities = item.entities
          } else {
            delete eventItem.entities
          }
          pendingForApplyMessagesByEventId.set(eventId, {
            ...event,
            item: eventItem,
          })
        }
      }
      _triggerStorageRef(pServed)
    }
  }

  const updateMessageStatus = (
    eventId: number,
    pServed: string,
    contactId: string,
    status: MessageStatusType,
    rank = 0,
  ) => {
    if (contactId === currentUser.value?.imLogin) {
      useNotificationItemStore().updateUnread(pServed, eventId)
    }

    const isCurrentUser = currentUser.value?.imLogin === contactId || currentUser.value?.imLogin === pServed
    if (isCurrentUser) {
      const message = findById(eventId)
      if (message) {
        if (isMessageDeleted(message) && status !== MessageStatusType.DELETED) {
          return
        }

        const updated = { ...message, status } as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
        eventsById.value.set(eventId, updated)
        const index = eventsByRoomPServed.value[pServed]?.findIndex((e) => e.eventId === eventId)
        if (index !== undefined && index >= 0) {
          eventsByRoomPServed.value[pServed]![index] = updated
        }
        const rankMap = eventsByRoomPServedAndRank.value[pServed]
        if (rankMap && message.rank !== undefined && rankMap[message.rank]) {
          eventsByRoomPServedAndRank.value[pServed]![message.rank] = updated
        }
      } else {
        const update: MessageMarkedAsRead = {
          type: 'MessageMarkedAsRead',
          eventId,
          rank,
          timestamp: 0,
          from: pServed,
          eventIdMarkedAsRead: eventId,
          executor: contactId,
          pServed: pServed,
        }
        pendingForApplyMessagesByEventId.set(eventId, {
          eventId, status, timestamp: 0, item: update, rank, type: 0,
        })
      }
    }
    _triggerStorageRef(pServed)
  }

  const isCurrentUserMentionedOrQuotedAtUnreadMessages = (pServed: PServed) => {
    const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(pServed)
    const currentImLogin = currentUser.value?.imLogin
    return !!findRoomMessages(pServed).filter(isTextMessage).find(({ rank, item: { mentionedUsers, quote }}) => {
      if (rank <= lastReadMessageRank) {
        return false
      }
      if (!!mentionedUsers?.find((s) => s === currentImLogin)) {
        return true
      }
      if (quote?.quotedEventId) {
        const quotedMessage = findById(quote.quotedEventId)  // todo if not in history
        return quotedMessage?.item.type === 'OutgoingMessageEvent'
      }
      return false
    })
  }

  const getOldestStoredRank = (pServed: PServed): number | null => {
    const raw = eventsByRoomPServed.value[pServed]
    if (!raw?.length) {
      return null
    }

    let minRank: number | null = null
    raw.forEach((message) => {
      if (message.rank === undefined) {
        return
      }
      minRank = minRank === null ? message.rank : Math.min(minRank, message.rank)
    })

    return minRank
  }

  const hasMoreOlderHistory = (pServed: PServed): boolean => {
    const viewport = getRoomHistoryViewport(pServed)
    const rawMessages = eventsByRoomPServed.value[pServed] ?? []

    if (viewport.mode === 'anchored') {
      return viewport.hasMoreOlder
    }

    const primaryTailBounds = getPrimaryTailSegmentBounds(rawMessages)
    if (!primaryTailBounds) {
      return false
    }

    if (primaryTailBounds.minRank > 1) {
      return true
    }

    return hasRankGapBeforeStoredHead(rawMessages, primaryTailBounds.minRank)
  }

  const getMinStoredRankAbove = (messages: HistoryMessageItem[], rank: number): number | null => {
    let minRankAbove: number | null = null

    messages.forEach((message) => {
      if (message.rank === undefined || message.rank <= rank) {
        return
      }

      minRankAbove = minRankAbove === null ? message.rank : Math.min(minRankAbove, message.rank)
    })

    return minRankAbove
  }

  const hasRankGapBeforeStoredTail = (messages: HistoryMessageItem[], viewportMaxRank: number): boolean => {
    const minRankAbove = getMinStoredRankAbove(messages, viewportMaxRank)
    return minRankAbove !== null && minRankAbove > viewportMaxRank + 1
  }

  const getMaxStoredRankBelow = (messages: HistoryMessageItem[], rank: number): number | null => {
    let maxRankBelow: number | null = null

    messages.forEach((message) => {
      if (message.rank === undefined || message.rank >= rank) {
        return
      }

      maxRankBelow = maxRankBelow === null ? message.rank : Math.max(maxRankBelow, message.rank)
    })

    return maxRankBelow
  }

  const hasRankGapBeforeStoredHead = (messages: HistoryMessageItem[], viewportMinRank: number): boolean => {
    const maxRankBelow = getMaxStoredRankBelow(messages, viewportMinRank)
    return maxRankBelow !== null && maxRankBelow < viewportMinRank - 1
  }

  const getSortedStoredRanksInRange = (
    messages: HistoryMessageItem[],
    minRank: number,
    maxRank: number,
  ): number[] => {
    return messages
      .map((message) => message.rank)
      .filter((rank): rank is number => rank !== undefined && rank >= minRank && rank <= maxRank)
      .sort((left, right) => left - right)
  }

  const hasInternalRankGapInRange = (
    messages: HistoryMessageItem[],
    minRank: number,
    maxRank: number,
  ): boolean => {
    const ranks = getSortedStoredRanksInRange(messages, minRank, maxRank)

    if (ranks.length <= 1) {
      return false
    }

    let previousRank = ranks[0]
    if (previousRank === undefined) {
      return false
    }

    for (let index = 1; index < ranks.length; index++) {
      const currentRank = ranks[index]
      if (currentRank === undefined) {
        continue
      }

      if (currentRank - previousRank > PRIMARY_TAIL_MAX_RANK_HOLE) {
        return true
      }

      previousRank = currentRank
    }

    return false
  }

  const getInternalGapUpperBoundRank = (
    messages: HistoryMessageItem[],
    minRank: number,
    maxRank: number,
  ): number | null => {
    const ranks = getSortedStoredRanksInRange(messages, minRank, maxRank)

    for (let index = 1; index < ranks.length; index++) {
      const previousRank = ranks[index - 1]
      const currentRank = ranks[index]
      if (previousRank === undefined || currentRank === undefined) {
        continue
      }

      if (currentRank - previousRank > PRIMARY_TAIL_MAX_RANK_HOLE) {
        return currentRank
      }
    }

    return null
  }

  const getInternalGapLowerBoundRank = (
    messages: HistoryMessageItem[],
    minRank: number,
    maxRank: number,
  ): number | null => {
    const ranks = getSortedStoredRanksInRange(messages, minRank, maxRank)

    for (let index = ranks.length - 2; index >= 0; index--) {
      const currentRank = ranks[index]
      const nextRank = ranks[index + 1]
      if (currentRank === undefined || nextRank === undefined) {
        continue
      }

      if (nextRank - currentRank > PRIMARY_TAIL_MAX_RANK_HOLE) {
        return currentRank
      }
    }

    return null
  }

  const getNewestStoredMessageAtOrBeforeRank = (
    messages: HistoryMessageItem[],
    maxRank: number,
  ): HistoryMessageItem | undefined => {
    let newest: HistoryMessageItem | undefined

    messages.forEach((message) => {
      if (message.rank === undefined || message.rank > maxRank) {
        return
      }

      if (!newest || (message.rank ?? 0) > (newest.rank ?? 0)) {
        newest = message
      }
    })

    return newest
  }

  const getOldestStoredMessageAtOrAfterRank = (
    messages: HistoryMessageItem[],
    minRank: number,
  ): HistoryMessageItem | undefined => {
    let oldest: HistoryMessageItem | undefined

    messages.forEach((message) => {
      if (message.rank === undefined || message.rank < minRank) {
        return
      }

      if (!oldest || (message.rank ?? 0) < (oldest.rank ?? 0)) {
        oldest = message
      }
    })

    return oldest
  }

  const extendMaxRankThroughAdjacentStoredRanks = (
    messages: HistoryMessageItem[],
    maxRank: number,
    minRank: number | null = null,
  ): number => {
    const storedRanks = new Set(
      messages
        .map((message) => message.rank)
        .filter((rank): rank is number => rank !== undefined),
    )

    let nextMaxRank = maxRank
    while (storedRanks.has(nextMaxRank + 1)) {
      nextMaxRank++
    }

    if (
      minRank !== null
      && nextMaxRank > maxRank
      && hasInternalRankGapInRange(messages, minRank, nextMaxRank)
    ) {
      return maxRank
    }

    return nextMaxRank
  }

  const extendMinRankThroughAdjacentStoredRanks = (
    messages: HistoryMessageItem[],
    minRank: number,
    maxRank: number | null = null,
  ): number => {
    const storedRanks = new Set(
      messages
        .map((message) => message.rank)
        .filter((rank): rank is number => rank !== undefined),
    )

    let nextMinRank = minRank
    while (storedRanks.has(nextMinRank - 1)) {
      nextMinRank--
    }

    if (
      maxRank !== null
      && nextMinRank < minRank
      && hasInternalRankGapInRange(messages, nextMinRank, maxRank)
    ) {
      return minRank
    }

    return nextMinRank
  }

  const resolveAnchoredHasMoreNewer = (
    messages: HistoryMessageItem[],
    viewportMaxRank: number | null,
    viewportMinRank: number | null,
    fetchedCount: number,
    batchSize: number,
  ): boolean => {
    if (
      viewportMinRank !== null
      && viewportMaxRank !== null
      && hasInternalRankGapInRange(messages, viewportMinRank, viewportMaxRank)
    ) {
      return true
    }

    if (fetchedCount >= batchSize) {
      return true
    }

    if (viewportMaxRank === null) {
      return false
    }

    return hasRankGapBeforeStoredTail(messages, viewportMaxRank)
  }

  const resolveAnchoredHasMoreOlder = (
    messages: HistoryMessageItem[],
    viewportMinRank: number | null,
    viewportMaxRank: number | null,
    fetchedCount: number,
    batchSize: number,
  ): boolean => {
    if (
      viewportMinRank !== null
      && viewportMaxRank !== null
      && hasInternalRankGapInRange(messages, viewportMinRank, viewportMaxRank)
    ) {
      return true
    }

    if (viewportMinRank === null || viewportMinRank <= 1) {
      return false
    }

    if (fetchedCount >= batchSize) {
      return true
    }

    if (hasRankGapBeforeStoredHead(messages, viewportMinRank)) {
      return true
    }

    return viewportMinRank > 1
  }

  const getHistoryCursorEventId = (pServed: PServed, eventId?: number): number | undefined => {
    if (eventId) {
      return eventId
    }

    const viewport = getRoomHistoryViewport(pServed)
    const rawMessages = eventsByRoomPServed.value[pServed] ?? []

    if (viewport.mode === 'anchored' && viewport.minRank !== null) {
      if (viewport.maxRank !== null) {
        const gapUpperRank = getInternalGapUpperBoundRank(rawMessages, viewport.minRank, viewport.maxRank)
        if (gapUpperRank !== null) {
          const gapCursorMessage = getOldestStoredMessageAtOrAfterRank(rawMessages, gapUpperRank)
          if (gapCursorMessage?.eventId) {
            return gapCursorMessage.eventId
          }
        }
      }

      const cursorMessage = getOldestStoredMessageAtOrAfterRank(rawMessages, viewport.minRank)
      return cursorMessage?.eventId ?? findRoomMessages(pServed)[0]?.eventId
    }

    if (viewport.mode === 'tail') {
      const primaryTailBounds = getPrimaryTailSegmentBounds(rawMessages)
      if (primaryTailBounds) {
        const { minRank, maxRank } = primaryTailBounds
        const gapUpperRank = getInternalGapUpperBoundRank(rawMessages, minRank, maxRank)
        if (gapUpperRank !== null) {
          const gapCursorMessage = getOldestStoredMessageAtOrAfterRank(rawMessages, gapUpperRank)
          if (gapCursorMessage?.eventId) {
            return gapCursorMessage.eventId
          }
        }

        const cursorMessage = getOldestStoredMessageAtOrAfterRank(rawMessages, minRank)
        return cursorMessage?.eventId ?? findRoomMessages(pServed)[0]?.eventId
      }
    }

    const rawOldest = rawMessages[0]
    const visibleOldest = findRoomMessages(pServed)[0]

    if (rawOldest?.eventId !== undefined && visibleOldest?.eventId !== undefined) {
      const rawRank = rawOldest.rank ?? Number.MAX_SAFE_INTEGER
      const visibleRank = visibleOldest.rank ?? Number.MAX_SAFE_INTEGER

      if (rawRank < visibleRank) {
        return rawOldest.eventId
      }

      return visibleOldest.eventId
    }

    if (visibleOldest?.eventId) {
      return visibleOldest.eventId
    }

    // В сторе могут остаться только удалённые сообщения (tombstone) — findRoomMessages их скрывает.
    // Берём самый старый raw-event как cursor, чтобы запросить более старую видимую историю.
    if (rawOldest?.eventId) {
      return rawOldest.eventId
    }

    return deletedEventIds.value.get(pServed)
  }

  const prefetchHistory = async (pServed: PServed) => {
    await chatStoreIsReady
    const unread = getUnreadCount(pServed)
    const messagesCount = findRoomMessages(pServed).length
    const notInHistory = unread - messagesCount
    const notInRenderLimit = REQUEST_HISTORY_MESSAGES_LIMIT - messagesCount
    let messages
    if (notInHistory > 0) {
      messages = await fetchHistory(pServed, notInHistory)
    } else if (notInRenderLimit > 0) {
      messages = await fetchHistory(pServed, notInRenderLimit)
    }
    if (messages) {
      addMessages(pServed, messages)
    }
    return Promise.resolve(true)
  }

  const getNewerHistoryCursorEventId = (pServed: PServed, eventId?: number): number | undefined => {
    if (eventId) {
      return eventId
    }

    const viewport = getRoomHistoryViewport(pServed)
    const rawMessages = eventsByRoomPServed.value[pServed] ?? []

    if (viewport.mode === 'anchored' && viewport.maxRank !== null) {
      if (viewport.minRank !== null) {
        const gapLowerRank = getInternalGapLowerBoundRank(rawMessages, viewport.minRank, viewport.maxRank)
        if (gapLowerRank !== null) {
          const gapCursorMessage = getNewestStoredMessageAtOrBeforeRank(rawMessages, gapLowerRank)
          if (gapCursorMessage?.eventId) {
            return gapCursorMessage.eventId
          }
        }
      }

      const cursorMessage = getNewestStoredMessageAtOrBeforeRank(rawMessages, viewport.maxRank)
      return cursorMessage?.eventId ?? findRoomMessages(pServed).at(-1)?.eventId
    }

    const rawNewest = rawMessages.at(-1)
    const visibleNewest = findRoomMessages(pServed).at(-1)

    if (rawNewest?.eventId !== undefined && visibleNewest?.eventId !== undefined) {
      const rawRank = rawNewest.rank ?? 0
      const visibleRank = visibleNewest.rank ?? 0

      if (rawRank > visibleRank) {
        return rawNewest.eventId
      }

      return visibleNewest.eventId
    }

    return rawNewest?.eventId ?? visibleNewest?.eventId
  }

  const requestRoomHistory = async (
    pServed: PServed,
    messagesLimit: number,
    cursorEventId: number,
    type: 'older' | 'newer' = 'older',
  ): Promise<HistoryMessageItem[]> => {
    const request: RequestChatHistoryPayload = {
      pServed,
      isConference: pServedIsRoom(pServed),
      cursorEventId,
      type,
      messagesLimit,
    }
    const history = await requestChatHistory(request)
    useReactionsStore().setReactionsByHistory(history)

    return history.events
  }

  const fetchHistory = async (
    pServed: string,
    messagesLimit: number = REQUEST_HISTORY_MESSAGES_LIMIT,
    eventId?: number,
    type: 'older' | 'newer' = 'older',
  ): Promise<HistoryMessageItem[]> => {
    const { currentGuest } = useGuestAuth()

    try {
      const cursorEventId = type === 'newer'
        ? getNewerHistoryCursorEventId(pServed, eventId)
        : getHistoryCursorEventId(pServed, eventId)

      const canRequest = currentGuest.value === undefined ? cursorEventId !== undefined : true

      if (canRequest && cursorEventId !== undefined) {
        return requestRoomHistory(pServed, messagesLimit, cursorEventId, type)
      }
      return Promise.resolve([])
    } catch (e) {
      return Promise.reject(e)
    }
  }

  /**
   * Догрузка событий новее cursor (htMessageByContactToFuture).
   * Нужна после сна/реконнекта: requestDialogs даёт только tip, а type:older идёт от самого старого и дырку в середине не закрывает.
   */
  const fetchNewerHistory = async (
    pServed: PServed,
    cursorEventId: number,
    messagesLimit: number = REQUEST_HISTORY_MESSAGES_LIMIT,
  ): Promise<HistoryMessageItem[]> => {
    const request: RequestChatHistoryPayload = {
      pServed,
      isConference: pServedIsRoom(pServed),
      cursorEventId,
      type: 'newer',
      messagesLimit,
    }
    const history = await requestChatHistory(request)
    useReactionsStore().setReactionsByHistory(history)
    return history.events
  }

  const syncRoomHistoryFromCursor = async (pServed: PServed, fromEventId: number) => {
    let cursorEventId = fromEventId

    for (let batch = 0; batch < SYNC_NEWER_MAX_BATCHES; batch++) {
      let events: HistoryMessageItem[]
      try {
        events = await fetchNewerHistory(pServed, cursorEventId)
      } catch (e) {
        console.error(e)
        break
      }

      if (!events.length) {
        break
      }

      addMessages(pServed, events)

      const nextCursor = events.reduce(
        (maxId, event) => (event.eventId > maxId ? event.eventId : maxId),
        cursorEventId,
      )
      if (nextCursor === cursorEventId) {
        break
      }

      cursorEventId = nextCursor
      if (events.length < REQUEST_HISTORY_MESSAGES_LIMIT) {
        break
      }
    }
  }

  const fetchHistoryNewer = async (
    pServed: PServed,
    messagesLimit: number = LAZY_LOAD_NEWER_BATCH_SIZE,
    eventId?: number,
  ): Promise<HistoryMessageItem[]> => {
    return fetchHistory(pServed, messagesLimit, eventId, 'newer')
  }

  const fetchMessageByEventId = async (pServed: PServed, eventId: number): Promise<HistoryMessageItem | undefined> => {
    const events = await requestRoomHistory(pServed, 1, eventId)
    return events.find((event) => event.eventId === eventId) ?? events[0]
  }

  const loadViewportAroundEvent = async (pServed: PServed, eventId: number): Promise<boolean> => {
    await chatStoreIsReady
    const { startLoading, stopLoading } = useChatHistoryLoading()

    try {
      startLoading()

      const targetMessage = await fetchMessageByEventId(pServed, eventId)
      if (!targetMessage?.eventId) {
        return false
      }

      const [olderMessages, newerMessages] = await Promise.all([
        requestRoomHistory(pServed, VIEWPORT_AROUND_BATCH_SIZE, eventId, 'older'),
        requestRoomHistory(pServed, VIEWPORT_AROUND_BATCH_SIZE, eventId, 'newer'),
      ])

      const mergedMessages = dedupeHistoryByEventId([
        ...olderMessages,
        targetMessage,
        ...newerMessages,
      ])

      if (!mergedMessages.some((message) => message.eventId === eventId)) {
        return false
      }

      addMessages(pServed, mergedMessages)

      const rawMessages = eventsByRoomPServed.value[pServed] ?? []
      const { minRank, maxRank } = collectRankBounds(mergedMessages)
      const nextMinRank = minRank !== null && maxRank !== null
        ? extendMinRankThroughAdjacentStoredRanks(rawMessages, minRank, maxRank)
        : minRank
      const nextMaxRank = maxRank !== null && minRank !== null
        ? extendMaxRankThroughAdjacentStoredRanks(rawMessages, maxRank, minRank)
        : maxRank

      setRoomHistoryViewport(pServed, {
        mode: 'anchored',
        minRank: nextMinRank,
        maxRank: nextMaxRank,
        anchorEventId: eventId,
        hasMoreOlder: resolveAnchoredHasMoreOlder(
          rawMessages,
          nextMinRank,
          nextMaxRank,
          olderMessages.length,
          VIEWPORT_AROUND_BATCH_SIZE,
        ),
        hasMoreNewer: resolveAnchoredHasMoreNewer(
          rawMessages,
          nextMaxRank,
          nextMinRank,
          newerMessages.length,
          VIEWPORT_AROUND_BATCH_SIZE,
        ),
      })

      return true
    } catch (e) {
      console.error(e)
      return false
    } finally {
      await yieldToMainThread()
      stopLoading()
    }
  }

  const restoreTailViewport = async (pServed: PServed): Promise<void> => {
    await chatStoreIsReady
    const { startLoading, stopLoading } = useChatHistoryLoading()

    try {
      startLoading()
      setRoomHistoryViewport(pServed, createDefaultRoomHistoryViewport())

      const unread = getUnreadCount(pServed)
      const messagesLimit = Math.max(REQUEST_HISTORY_MESSAGES_LIMIT, unread)
      const history = await requestChatHistory({
        pServed,
        isConference: pServedIsRoom(pServed),
        messagesLimit,
      })
      useReactionsStore().setReactionsByHistory(history)

      const messages = history.events ?? []
      if (messages.length) {
        addMessages(pServed, messages)
      }
    } catch (e) {
      console.error(e)
    } finally {
      await yieldToMainThread()
      stopLoading()
    }
  }

  const updateViewportAfterOlderLoad = (
    pServed: PServed,
    appendedMessages: HistoryMessageItem[],
    batchSize: number = LAZY_LOAD_OLDER_BATCH_SIZE,
  ): void => {
    const viewport = getRoomHistoryViewport(pServed)
    if (viewport.mode !== 'anchored' || !appendedMessages.length) {
      return
    }

    const rawMessages = eventsByRoomPServed.value[pServed] ?? []
    const { minRank } = collectRankBounds(appendedMessages)
    const mergedMinRank = minRank !== null && viewport.minRank !== null
      ? Math.min(viewport.minRank, minRank)
      : (minRank ?? viewport.minRank)
    const nextMinRank = mergedMinRank !== null && viewport.maxRank !== null
      ? extendMinRankThroughAdjacentStoredRanks(rawMessages, mergedMinRank, viewport.maxRank)
      : mergedMinRank

    setRoomHistoryViewport(pServed, {
      ...viewport,
      minRank: nextMinRank,
      hasMoreOlder: resolveAnchoredHasMoreOlder(
        rawMessages,
        nextMinRank,
        viewport.maxRank,
        appendedMessages.length,
        batchSize,
      ),
    })
  }

  const updateViewportAfterNewerLoad = (pServed: PServed, appendedMessages: HistoryMessageItem[], batchSize: number): void => {
    const viewport = getRoomHistoryViewport(pServed)
    if (viewport.mode !== 'anchored' || !appendedMessages.length) {
      return
    }

    const rawMessages = eventsByRoomPServed.value[pServed] ?? []
    const { maxRank } = collectRankBounds(appendedMessages)
    const mergedMaxRank = maxRank !== null && viewport.maxRank !== null
      ? Math.max(viewport.maxRank, maxRank)
      : (maxRank ?? viewport.maxRank)
    const nextMaxRank = mergedMaxRank !== null && viewport.minRank !== null
      ? extendMaxRankThroughAdjacentStoredRanks(rawMessages, mergedMaxRank, viewport.minRank)
      : mergedMaxRank

    setRoomHistoryViewport(pServed, {
      ...viewport,
      maxRank: nextMaxRank,
      hasMoreNewer: resolveAnchoredHasMoreNewer(
        rawMessages,
        nextMaxRank,
        viewport.minRank,
        appendedMessages.length,
        batchSize,
      ),
    })
  }

  const addMessages = (pServed: string, messages: HistoryMessageItem[]) => {
    const isNewRoomBucket = !(pServed in eventsByRoomPServedData)
    messages.forEach((event: HistoryMessageItem) => _addMessageWithoutTriggerRef(pServed, event))
    _triggerStorageRef(pServed, { exposeRoomsIndex: isNewRoomBucket })
  }

  const cleanupOldRoomMessages = (pServed: PServed) => {
    if (getRoomHistoryViewport(pServed).mode === 'anchored') {
      return
    }

    const messages = findRoomMessages(pServed).filter((message) => !isPendingClientOutgoing(message))
    const unread = getUnreadCount(pServed)
    if (messages.length < (unread + REQUEST_HISTORY_MESSAGES_LIMIT * 2)) {
      return
    }

    const toDeleteIndex = messages.length - Math.max(REQUEST_HISTORY_MESSAGES_LIMIT, unread)
    const minRankToKeep = messages[toDeleteIndex]?.rank
    if (minRankToKeep === undefined) {
      return
    }

    const rawMessages = eventsByRoomPServed.value[pServed] ?? []
    rawMessages
      .filter((message) => message.rank !== undefined && message.rank < minRankToKeep)
      .forEach(({ eventId, rank }) => {
        removeEventFromQuotedIndex(eventId)
        const eventForDelete = eventsById.value.get(eventId) as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined
        removeEventFromForwardingIndex(eventForDelete)
        eventsById.value.delete(eventId)
        deletedEventsByEventId.value.delete(eventId)
        roomIdByEventId.value.delete(eventId)
        eventsByRoomPServed.value[pServed] = eventsByRoomPServed.value[pServed]!.filter((m) => m.eventId !== eventId)
        delete rankByEventId.value[eventId]
        eventsByForwardingEventId.value.delete(eventId)
        eventIdsByQuotedEventId.value.delete(eventId)
        if (rank !== undefined) {
          delete eventsByRoomPServedAndRank.value[pServed]?.[rank]
        }
      })
    _triggerStorageRef(pServed)
  }

  const fetchAndAddHistory = async (pServed: string, messagesLimit: number = REQUEST_HISTORY_MESSAGES_LIMIT) => {
    const { startLoading, stopLoading } = useChatHistoryLoading()

    try {
      startLoading()
      const history = await fetchHistory(pServed, messagesLimit)
      if (history) {
        addMessages(pServed, history)
      }
      return history
    } catch (e) {
      return Promise.reject(e)
    } finally {
      await yieldToMainThread()
      stopLoading()
    }
  }

  const getDeletedEvent = (eventId: HistoryMessageItem['eventId']) => {
    const fromIndex = deletedEventsByEventId.value.get(eventId)
    if (fromIndex) {
      return fromIndex
    }

    const event = eventsById.value.get(eventId)
    return event?.status === MessageStatusType.DELETED ? event : undefined
  }

  /**
   * Feature «sync-im-history» (или «im-session-init») или или отдельный app-level модуль инициализации IM
   *
   * Один слой фичи, который знает про useIM(), userImIsReady, requestDialogs, onHistory и решает:
   * когда запросить диалоги, когда применить пришедшую историю к комнатам,
   * контактам и к сторам сообщений/реакций/звонков.
   * Entities по-прежнему только принимают «готовые» данные.
   */
  const { userImIsReady } = storeToRefs(useIMStore())
  watch(userImIsReady, (value) => {
    if (value) {
      /** Последний локальный eventId по комнате до dialogs — до любых merge из onHistory/ответа */
      const lastEventIdByRoomBeforeDialogs = new Map<PServed, number>()
      Object.keys(eventsByRoomPServed.value).forEach((pServed) => {
        const last = findLastMessage(pServed)
        if (last?.eventId) {
          lastEventIdByRoomBeforeDialogs.set(pServed, last.eventId)
        }
      })

      requestDialogs().then(async (history: HistoryResponse) => {
        applyHistoryEvents(history)
        useReactionsStore().setReactionsByHistory(history)

        _triggerStorageRef()

        const syncFromEventIdByRoom = new Map<PServed, number>()
        lastEventIdByRoomBeforeDialogs.forEach((fromEventId, pServed) => {
          const tip = findLastMessage(pServed)
          if (tip?.eventId && tip.eventId !== fromEventId) {
            syncFromEventIdByRoom.set(pServed, fromEventId)
          }
        })

        if (syncFromEventIdByRoom.size) {
          await Promise.allSettled(
            [...syncFromEventIdByRoom.entries()].map(([pServed, fromEventId]) => (
              syncRoomHistoryFromCursor(pServed, fromEventId)
            )),
          )
        }

        _chatStoreIsReadyResolveFn(true)
        const unreadForBadge = totalUnreadCount.value
        if (unreadForBadge) {
          useAppBadge().setAppIconBadge(unreadForBadge)
        } else {
          useAppBadge().clearAppBadge().catch((e: Error) => console.error(e))
        }
      }).catch((e) => {
        console.error(e)
        _triggerStorageRef()
        _chatStoreIsReadyResolveFn(false)
      })
    }
  })

  watch(totalUnreadCount, (value, oldValue) => {
    const { setAppIconBadge, clearAppBadge } = useAppBadge()
    updateStatusIcon()
    if (oldValue && !value) {
      clearAppBadge().catch((e: Error) => console.error(e))
    } else if (value) {
      setAppIconBadge(value).catch((e: Error) => console.error(e))
    }
  })

  const onMessageEventReceived = (event: BaseByOtherEvent | MessageCallReceived) => {
    const callTypedEvent = event as MessageCallReceived
    if (
      event.type === 'MessageCallReceived' &&
      !isCallMessage({ item: event }) ||
      event.type === 'ConferenceRemoved' ||
      callTypedEvent.event === 'call_answered'
    ) {
      return
    }

    const { timestamp, eventId, pServed, rank, type } = event
    if (isSystemContactPServed(pServed)) {
      return
    }

    const modifiedEvent = { ...event }
    // Для чата Сохраненные сообщения, в котором мы пишем сами себе
    if (type === 'IncomingMessageEvent' && event.executor === currentUser?.value?.imLogin && event.pServed === currentUser.value?.imLogin) {
      modifiedEvent.type = 'OutgoingMessageEvent'
    }

    addMessage(pServed, {
      eventId, type: 0, timestamp, rank,
      status: MessageStatusType.ACCEPTED,
      item: modifiedEvent,
    })

    if (type === 'MessageCallReceived') {
      const callEvent = event as MessageCallReceived
      const isOutgoingCall =
        'srcPServed' in callEvent &&
        !!currentUser.value?.imLogin &&
        callEvent.srcPServed === currentUser.value.imLogin

      // call_ended — всегда; исходящие (недозвон/отмена с этого или другого аппарата) — сразу прочитаны
      if (callEvent.event === 'call_ended' || isOutgoingCall) {
        markAsRead({
          eventIdMarkedAsRead: eventId,
          to: pServed,
        }).then(result => {
          const userId = currentUser.value?.imLogin ?? currentGuest.value?.pServed
          if (result && userId) {
            updateLastReadEventId(pServed, userId, eventId, rank)
          }
        })
      }
    }
  }

  const getPreviousEventByRank = (pServed: PServed, rank: Rank): HistoryMessageItem | undefined => {
    let candidateRank = rank - 1
    if (candidateRank < 0) return undefined

    const eventsByPServed = eventsByRoomPServedAndRank.value[pServed]
    if (!eventsByPServed) return undefined

    while (candidateRank >= 0) {
      const candidate = eventsByPServed[candidateRank]
      if (candidate) return candidate
      candidateRank -= 1
    }
    return undefined
  }

  const deleteEventByRank = (rank: Rank, pServed: PServed) => {
    if (eventsByRoomPServedAndRank.value[pServed]) {
      delete eventsByRoomPServedAndRank.value[pServed][rank]
    }
  }

  onMessage(onMessageEventReceived)
  onConferenceCreated(onMessageEventReceived)
  onConferenceEdited(onMessageEventReceived)
  onPinMessage(onMessageEventReceived)
  onUnpinMessage(onMessageEventReceived)

  onEditMessage((event: MessageUpdatedByOtherEvent) => {
    const { timestamp, eventId, rank } = event
    const status = eventsById.value.get(eventId)?.status ?? MessageStatusType.ACCEPTED
    updateMessageText({
      eventId, type: 0, timestamp, rank,
      status,
      item: event,
    })
  })

  /**
   * Применяет удаление сообщения из IM.
   * Используется и для onDeleteMessage (удаление другим), и для HistoryResponse с DeleteMessageEvent
   * (в т.ч. удаление «собой» на другой сессии той же учётной записи).
   */
  const applyDeleteMessageEvent = (event: MessageDeletedByOtherEvent | MessageDeletedByYouEvent) => {
    const roomKey = 'from' in event ? event.from : event.pServed
    const message = eventsById.value.get(event.targetEventId)
    if (message) {
      deleteEventByRank(message.rank, roomKey)
    }

    _deleteMessage(event.targetEventId, event.pServed)
    handleDeletedMessage(roomKey, event.targetEventId)
  }

  onDeleteMessage((event: MessageDeletedByOtherEvent) => {
    applyDeleteMessageEvent(event)
  })

  const applyHistoryEvents = (history: HistoryResponse) => {
    const _contactPServedsForFetch: PServed[] = []
    const pendingGroupStatusEvents: GroupStatusEvent[] = []
    const roomsToReconcileReadMarks = new Set<PServed>()
    history.events.forEach((event) => {
      const { item: { pServed }} = event
      if (isSystemContactPServed(pServed)) {
        return
      }

      if (event.item.type === 'GroupStatusEvent' && pServed) {
        const roomId = fromPServedUserIfRoom(pServed)
        const contact = useStoreContacts().findByPServed(roomId)
        if (contact) {
          createPrivateRoom(roomId, contact)
        } else if (!pServedIsRoom(pServed)) {
          _contactPServedsForFetch.push(pServed)
        }
        pendingGroupStatusEvents.push(event.item as GroupStatusEvent)
      } else if (event.item.type === 'DeleteMessageEvent') {
        // Realtime-удаление через HistoryResponse (например, та же учётка во втором браузере)
        applyDeleteMessageEvent(event.item as MessageDeletedByOtherEvent | MessageDeletedByYouEvent)
      } else if (pServed) {
        const { item: { type }, eventId } = event
        const isNotMyCallMessage = isCallMessage(event) && currentUser?.value?.imLogin !== pServed
        if (isMessageNotReactionOrNotification(event) && (event.item.type !== 'MessageCallReceived' || isNotMyCallMessage || event.item.event === 'appointment_start')) {
          _addMessageWithoutTriggerRef(pServed, event)
          const currentImLogin = currentUser.value?.imLogin
          if (currentImLogin && type === 'OutgoingMessageEvent' && !isMessageDeleted(event)) {
            updateMessageStatus(
              eventId,
              pServed,
              currentImLogin,
              MessageStatusType.READ,
            )
          }
        }
      }
    })
    pendingGroupStatusEvents.forEach((typedEvent) => {
      if (typedEvent.extendedParams.isEnabled || !pServedIsRoom(typedEvent.pServed)) {
        useStoreRooms().updateFromContactStatus(typedEvent)
        useRoomScheduleStore().updateFromContactStatus(typedEvent)
        updateFromContactStatus(typedEvent)
        roomsToReconcileReadMarks.add(typedEvent.pServed)
      }
    })
    roomsToReconcileReadMarks.forEach((pServed) => {
      useStoreLastReadEventIds().reconcileReadMarksForRoom(pServed)
    })
    useStoreContacts().fetchContactsBySomeIds(_contactPServedsForFetch, 'imLogin', true)
      .then((contacts: Contact[] | undefined) => {
        contacts?.forEach(c => createPrivateRoom(c.imLogin, c))
      })
  }

  onHistory((history: HistoryResponse) => {
    applyHistoryEvents(history)
    _triggerStorageRef()
  })

  onMarkAsRead((event: MessageMarkedAsRead) => {
    updateMessageStatus(
      event.eventIdMarkedAsRead,
      event.pServed,
      event.executor,
      MessageStatusType.READ,
    )
  })

  return {
    chatStoreIsReady,
    eventsByRoomPServed: computed(() => eventsByRoomPServed.value),
    eventIdsByQuotedEventId: computed(() => new Map(eventIdsByQuotedEventId.value)),
    $reset,
    findRoomMessages,
    findById,
    findLastMessage,
    findLastReadableMessageAtOrBefore,
    getUnreadCount,
    invalidateUnreadCount,
    totalUnreadCount,
    addMessage,
    updateMessage,
    updateMessageText,
    updateMessageStatus,
    deleteMessages,
    fetchAndAddHistory,
    fetchHistory,
    fetchHistoryNewer,
    addMessages,
    prefetchHistory,
    resetMessage,
    hideMessageForSelf,
    isCurrentUserMentionedOrQuotedAtUnreadMessages,
    getPreviousEventByRank,
    cleanupOldRoomMessages,
    getDeletedEvent,
    getRoomHistoryViewport,
    isEventInViewport,
    loadViewportAroundEvent,
    restoreTailViewport,
    updateViewportAfterOlderLoad,
    updateViewportAfterNewerLoad,
    clearRoomMessages,
    hasMoreOlderHistory,
    getOldestStoredRank,
  }
})
